import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNavigateWithContentFlush } from '@/hooks';
import { toContentHref } from '@/utils/content-ref';
import { Document } from '@vivport/backend/types';
import { Info } from 'lucide-react';

export function DocumentHierarchyInfoButton({ document }: { document: Document }) {
  const navigate = useNavigateWithContentFlush();
  const tree = document.hierarchyTree;
  const directChildrenCount = tree && tree.children ? tree.children.length : 0;
  const rootCode = document.rootCode ?? document.code;
  const parentCode = document.parentCode;
  const isRoot = parentCode === null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Document hierarchy info" title="Document hierarchy info">
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="space-y-3">
          <p className="text-sm font-medium">Document hierarchy</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <p className="text-muted-foreground">
              Depth: <span className="text-foreground">{document.depth}</span>
            </p>
            <p className="text-muted-foreground">
              Order: <span className="text-foreground">{document.sortOrder}</span>
            </p>
            <p className="text-muted-foreground">
              Path: <span className="text-foreground">{document.path ?? 'n/a'}</span>
            </p>
            <p className="col-span-2 text-muted-foreground">
              Direct sections: <span className="text-foreground">{directChildrenCount}</span>
            </p>
          </div>
          <div className="space-y-1 text-xs">
            <p className="text-muted-foreground">
              Root:{' '}
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() => navigate(toContentHref(rootCode))}
              >
                {`Document ${rootCode}`}
              </button>
            </p>
            <p className="text-muted-foreground">
              Parent:{' '}
              {!isRoot && parentCode ? (
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={() => navigate(toContentHref(parentCode))}
                >
                  {`Document ${parentCode}`}
                </button>
              ) : (
                <span className="text-foreground">This document is the root</span>
              )}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
